import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Ticket, Plus, ChatCircleDots, Clock, CheckCircle, Warning, ArrowLeft, PaperPlaneTilt } from '@phosphor-icons/react';
import SortableTable from '../components/SortableTable';
import axios from 'axios';
import { toast } from 'sonner';
import { useSellerMode } from '../contexts/SellerModeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONF = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700 border-green-200' },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const PRIORITY_CONF = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

export default function TicketsPage() {
  const { activeSeller } = useSellerMode();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [formData, setFormData] = useState({ subject: '', description: '', category: 'general', priority: 'medium', order_id: '' });

  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tickets`, { withCredentials: true });
      setTickets(res.data.tickets || []);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  }, []);

  // Re-fetch tickets when seller mode changes
  useEffect(() => { 
    const timer = setTimeout(() => {
      setLoading(true);
      fetchTickets();
    }, 100);
    return () => clearTimeout(timer);
  }, [fetchTickets, activeSeller?.user_id]);

  const handleCreate = async () => {
    if (!formData.subject.trim() || !formData.description.trim()) { toast.error('Subject and description required'); return; }
    try {
      await axios.post(`${API_URL}/api/tickets`, formData, { withCredentials: true });
      toast.success('Ticket created');
      setDialogOpen(false);
      setFormData({ subject: '', description: '', category: 'general', priority: 'medium', order_id: '' });
      fetchTickets();
    } catch { toast.error('Failed to create ticket'); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    try {
      await axios.post(`${API_URL}/api/tickets/${selectedTicket.ticket_id}/reply`, { message: replyText }, { withCredentials: true });
      toast.success('Reply sent');
      setReplyText('');
      const res = await axios.get(`${API_URL}/api/tickets`, { withCredentials: true });
      const updated = (res.data.tickets || []).find(t => t.ticket_id === selectedTicket.ticket_id);
      if (updated) setSelectedTicket(updated);
      setTickets(res.data.tickets || []);
    } catch { toast.error('Failed to send reply'); }
  };

  if (selectedTicket) {
    return (
      <DashboardLayout title="Ticket Detail" subtitle={selectedTicket.ticket_id}>
        <button onClick={() => setSelectedTicket(null)} className="flex items-center gap-2 text-sm text-[#5a5c87] hover:text-[#0A0A0A] mb-6" data-testid="back-tickets">
          <ArrowLeft size={16} /> Back to tickets
        </button>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-[#e5e5ea] rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg">{selectedTicket.subject}</h3>
                <span className={`px-3 py-1 text-xs font-bold rounded border ${STATUS_CONF[selectedTicket.status]?.color}`}>
                  {STATUS_CONF[selectedTicket.status]?.label}
                </span>
              </div>
              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {selectedTicket.messages?.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'seller' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-4 py-3 rounded-xl text-sm ${
                      msg.role === 'seller' ? 'bg-[#7d64ff] text-white rounded-br-sm' : 'bg-[#f2f0ff] border border-[#e5e5ea] rounded-bl-sm'
                    }`}>
                      <p className="font-medium text-xs mb-1 opacity-70">{msg.sender}</p>
                      <p>{msg.content}</p>
                      <p className="text-[10px] mt-1 opacity-60">{new Date(msg.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
              {selectedTicket.status !== 'closed' && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-[#e5e5ea]">
                  <Input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type a reply..." className="flex-1" data-testid="ticket-reply-input"
                    onKeyDown={e => { if (e.key === 'Enter') handleReply(); }} />
                  <Button onClick={handleReply} disabled={!replyText.trim()} className="bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="ticket-reply-btn">
                    <PaperPlaneTilt size={18} />
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-white border border-[#e5e5ea] rounded-lg p-6 space-y-3 text-sm">
              <div><span className="text-[#5a5c87] text-xs uppercase tracking-wider">Category</span><p className="font-semibold capitalize">{selectedTicket.category}</p></div>
              <div><span className="text-[#5a5c87] text-xs uppercase tracking-wider">Priority</span><p><span className={`px-2 py-0.5 rounded text-xs font-bold ${PRIORITY_CONF[selectedTicket.priority]}`}>{selectedTicket.priority}</span></p></div>
              {selectedTicket.order_id && <div><span className="text-[#5a5c87] text-xs uppercase tracking-wider">Order</span><p className="font-mono font-semibold">{selectedTicket.order_id}</p></div>}
              <div><span className="text-[#5a5c87] text-xs uppercase tracking-wider">Created</span><p>{new Date(selectedTicket.created_at).toLocaleString()}</p></div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Support Tickets" subtitle="Raise and track support requests"
      actions={<Button onClick={() => setDialogOpen(true)} className="bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="create-ticket-btn"><Plus size={18} className="mr-2" />New Ticket</Button>}>
      <div className="bg-white border border-[#e5e5ea] rounded-lg">
        {loading ? <div className="p-12 text-center"><div className="spinner mx-auto"></div></div> : (
          <SortableTable
            columns={[
              { key: 'ticket_id', label: 'ID', render: v => <span className="font-mono text-sm font-semibold">{v}</span> },
              { key: 'subject', label: 'Subject' },
              { key: 'category', label: 'Category', render: v => <span className="capitalize">{v}</span> },
              { key: 'priority', label: 'Priority', render: v => <span className={`px-2 py-0.5 rounded text-xs font-bold ${PRIORITY_CONF[v]}`}>{v}</span> },
              { key: 'status', label: 'Status', render: v => <span className={`px-2 py-1 text-xs font-bold rounded border ${STATUS_CONF[v]?.color}`}>{STATUS_CONF[v]?.label}</span> },
              { key: 'created_at', label: 'Created', render: v => new Date(v).toLocaleDateString() },
              { key: 'ticket_id', label: 'Action', sortable: false, render: (v, row) => (
                <Button variant="ghost" size="sm" onClick={() => setSelectedTicket(row)} data-testid={`view-ticket-${v}`}>
                  <ChatCircleDots size={16} className="mr-1" /> View
                </Button>
              )}
            ]}
            data={tickets}
            pageSize={10}
            emptyMessage="No tickets yet"
            emptyIcon={Ticket}
          />
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent><DialogHeader><DialogTitle className="font-bold">New Support Ticket</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Subject *</Label>
              <Input value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} className="mt-1" data-testid="ticket-subject" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Category</Label>
                <Select value={formData.category} onValueChange={v => setFormData({...formData, category: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="fulfillment">Fulfillment</SelectItem>
                    <SelectItem value="catalog">Catalog</SelectItem>
                    <SelectItem value="returns">Returns</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Priority</Label>
                <Select value={formData.priority} onValueChange={v => setFormData({...formData, priority: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Related Order ID (Optional)</Label>
              <Input value={formData.order_id} onChange={e => setFormData({...formData, order_id: e.target.value})} placeholder="ORD-XXXXX" className="mt-1" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Description *</Label>
              <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={4} className="mt-1" data-testid="ticket-description" /></div>
            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleCreate} className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="ticket-submit">Create Ticket</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
